import {
  Box,
  Container,
  Grid,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconBrain,
  IconBulb,
  IconCertificate,
  IconChartArrows,
  IconLock,
  IconUsers,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { PageContent } from "../../Model/PageContent.model";
// import Metrics from "../Metrics";
import GetHelpForm from "../Shared/GetHelpForm";
import "./Feature.css";

interface FeatureProps {
  pageContent?: PageContent;
}

export default function Feature({ pageContent }: FeatureProps) {
  const theme = useMantineTheme();
  const desktopMediaQuery = useMediaQuery(
    `(min-width: ${theme.breakpoints.md})`,
  );
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  const features = [
    {
      title: "Expert Guidance",
      icon: <IconBrain size={28} />,
      color: theme.colors.blue[6],
      gradient: `linear-gradient(135deg, ${theme.colors.blue[6]}, ${theme.colors.blue[4]})`,
    },
    {
      title: "Quick Support",
      icon: <IconChartArrows size={28} />,
      color: theme.colors.green[6],
      gradient: `linear-gradient(135deg, ${theme.colors.green[6]}, ${theme.colors.green[4]})`,
    },
    {
      title: "Personal Mentor",
      icon: <IconUsers size={28} />,
      color: theme.colors.teal[6],
      gradient: `linear-gradient(135deg, ${theme.colors.teal[6]}, ${theme.colors.teal[4]})`,
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setIsSliding(true);
      setTimeout(() => {
        setCurrentFeatureIndex((current) => (current + 1) % features.length);
        setIsSliding(false);
      }, 500);
    }, 3000);

    return () => clearInterval(timer);
  }, [features.length]);

  return (
    <Box className="feature-section" py={0}>
      <Container size={1000} px={desktopMediaQuery ? "xl" : "md"}>
        <Grid gutter={40} align="center">
          <Grid.Col span={{ base: 12, md: 5.5 }}>
            <Stack gap="xl">
              <Stack gap="md">
                <Title
                  className="gradient-text"
                  order={1}
                  size={desktopMediaQuery ? 45 : 32}
                  style={{ lineHeight: 1.2 }}
                >
                  Elevate Your Academic Excellence
                </Title>

                <Text size="xl" c="dimmed" style={{ maxWidth: 600 }}>
                  Learn with a dedicated tutor who helps you understand
                  concepts, build practical skills, and grow more confident in
                  your studies.
                </Text>

                {/* Mobile Form Section */}
                <Box hiddenFrom="md" mt="xl">
                  <Paper
                    shadow="xl"
                    radius="lg"
                    className="modern-form"
                    style={{
                      overflow: "hidden",
                      background: theme.white,
                      border: `1px solid rgba(${hexToRgb(
                        theme.colors.gray[2],
                      )}, 0.5)`,
                    }}
                  >
                    <GetHelpForm />
                  </Paper>
                </Box>

                <Box
                  className={`floating-animation ${
                    isSliding ? "blur-out-contract-bck" : "focus-in-expand-fwd"
                  }`}
                  style={{
                    minHeight: desktopMediaQuery ? "3.5rem" : "2.5rem",
                    display: "flex",
                    alignItems: "center",
                    marginTop: theme.spacing.lg,
                    background: `rgba(${hexToRgb(
                      features[currentFeatureIndex].color,
                    )}, 0.05)`,
                    padding: theme.spacing.md,
                    borderRadius: theme.radius.md,
                  }}
                >
                  <Group>
                    <ThemeIcon
                      size={56}
                      radius="md"
                      style={{
                        background: features[currentFeatureIndex].gradient,
                        boxShadow: `0 4px 14px 0 rgba(${hexToRgb(
                          features[currentFeatureIndex].color,
                        )}, 0.25)`,
                      }}
                    >
                      {features[currentFeatureIndex].icon}
                    </ThemeIcon>
                    <Text
                      size={desktopMediaQuery ? "28px" : "22px"}
                      fw={600}
                      c={features[currentFeatureIndex].color}
                    >
                      {features[currentFeatureIndex].title}
                    </Text>
                  </Group>
                </Box>

                {/* Trust Indicators */}
                <Box mt={10}>
                  <Stack gap="xs">
                    {[
                      {
                        icon: IconCertificate,
                        text: "Certified Experts",
                        color: theme.colors.indigo[7],
                        gradient: `linear-gradient(135deg, ${theme.colors.indigo[7]}, ${theme.colors.blue[5]})`,
                      },
                      {
                        icon: IconLock,
                        text: "100% Secure",
                        color: theme.colors.teal[7],
                        gradient: `linear-gradient(135deg, ${theme.colors.teal[7]}, ${theme.colors.green[5]})`,
                      },
                      {
                        icon: IconBulb,
                        text: "Smart Learning",
                        color: theme.colors.orange[7],
                        gradient: `linear-gradient(135deg, ${theme.colors.orange[7]}, ${theme.colors.yellow[5]})`,
                      },
                    ].map((item, index) => (
                      <Group
                        key={index}
                        wrap="nowrap"
                        gap="md"
                        className="feature-icon-wrapper"
                        style={{
                          padding: "8px 16px",
                          borderRadius: theme.radius.md,
                          background: `linear-gradient(to right, rgba(${hexToRgb(
                            item.color,
                          )}, 0.06), transparent)`,
                        }}
                      >
                        <ThemeIcon
                          size={44}
                          radius="xl"
                          style={{
                            background: item.gradient,
                            boxShadow: `0 4px 12px -4px rgba(${hexToRgb(
                              item.color,
                            )}, 0.4)`,
                          }}
                        >
                          <item.icon size={24} />
                        </ThemeIcon>
                        <Text size="sm" fw={600} c={item.color}>
                          {item.text}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Stack>
          </Grid.Col>

          {/* Desktop Form Section */}
          <Grid.Col span={{ base: 12, md: 6.5 }} visibleFrom="md">
            <Paper
              shadow="xl"
              radius="lg"
              className="modern-form"
              style={{
                overflow: "hidden",
                background: theme.white,
                maxWidth: 620,
                marginLeft: "auto",
                border: `1px solid rgba(${hexToRgb(
                  theme.colors.gray[2],
                )}, 0.5)`,
              }}
            >
              <GetHelpForm />
            </Paper>
          </Grid.Col>

          {/* Mobile Metrics */}
          {/* <Grid.Col span={12} hidden>
            <Metrics />
          </Grid.Col> */}
        </Grid>
      </Container>
    </Box>
  );
}

// Add this helper function at the end of the file, before the export
function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : "0, 0, 0";
}
